import Icon from '@/components/UI/Icon/Icon';
import Tooltip from '@/components/UI/Tooltip';
import { _isEmpty, _isNil, _isNotEmpty, _notNil, safelyDecodeURIComponent } from '@/littledash';
import cn from 'classnames';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { GroupBase, MultiValue, SingleValue } from 'react-select';
import { AsyncPaginate, type LoadOptions } from 'react-select-async-paginate';
import type {
  DataTableCellIndexCoordinate,
  DataTableCellSelectionChangeEvent,
  DataTableDirection,
  DataTableMetadataColumn,
} from '@/components/UI/DataTableV2/DataTable.model';
import { DataTableEvent } from '@/components/UI/DataTableV2/DataTable.model';
import {
  isMultiSelectFieldType,
  parsePaginationFromUrl,
  parseSelectValue,
} from '@/components/UI/DataTableV2/DataTable.util';
import { useDataTable } from '../DataTableProvider';
import { useCellCoordinate } from '../hooks/useCellCoordinate';
import { useCellData } from '../hooks/useCellData';
import { useCellEditability } from '../hooks/useCellEditability';
import { useCellMouseHandlers } from '../hooks/useCellMouseHandlers';
import { useCellPositioning } from '../hooks/useCellPositioning';
import { useCellSelectionState } from '../hooks/useCellSelectionState';
import { useCellState } from '../hooks/useCellState';
import { useGlobalEventListenerAbort } from '../hooks/useGlobalEventListenerAbort';
import { ApiService } from '@/support/ApiService';
import { notAborted, useAbortController } from '@/support/Hooks/fetch/useAbortController';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import InVivoError from '@/model/InVivoError';
import tableStyles from '../DataTableV2.module.scss';
import styles from './MetadataSelectCell.module.scss';

interface SelectOption {
  value: string;
  label: string;
}

interface MetadataSelectCellProps {
  coordinate: DataTableCellIndexCoordinate;
  positionOffset: { x: number; y: number };
}

const INITIAL_PAGINATION = { page: 1, perPage: 100 } as const;

// Static components config - removes dropdown indicator and separator
// The cell conditionally renders its own chevron icon when editable
const SELECT_COMPONENTS = {
  DropdownIndicator: null,
  IndicatorSeparator: null,
} as const;

export const MetadataSelectCell: FC<MetadataSelectCellProps> = ({ coordinate, positionOffset }) => {
  const { dataTableService } = useDataTable();
  const containerRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(false);
  const [editingOptions, setEditingOptions] = useState<Array<SelectOption>>([]);
  const selectedOptionsRef = useRef<Array<SelectOption>>([]);
  const keyboardNavigationDirection = useRef<DataTableDirection | null>(null);

  const { newAbortController } = useAbortController();

  const handleAbort = useCallback(() => {
    setActive(false);
  }, []);

  const { getSignal, abort } = useGlobalEventListenerAbort({ onAbort: handleAbort });

  const { idCoordinate, cellRef } = useCellCoordinate(coordinate);
  const { cellData } = useCellData(idCoordinate, cellRef);
  const { cellState, tooltip } = useCellState(idCoordinate, cellRef);
  const { selectionState } = useCellSelectionState(coordinate, idCoordinate);
  const { containerStyle, contentStyle } = useCellPositioning(positionOffset, active);
  const { isEditable } = useCellEditability(coordinate);

  const column = dataTableService.columnByIndex(coordinate.column) as DataTableMetadataColumn | undefined;
  const metadataConfig = column?.metadata;
  const glossaryApiId = metadataConfig?.metadata_glossary_api_id;
  const fieldType = metadataConfig?.field_type;
  const isMulti = isMultiSelectFieldType(fieldType);
  const isGroupBoundary = dataTableService.isGroupBoundary(coordinate.column);
  const hasOptions = _notNil(glossaryApiId);

  // Derive display options from cell data - parses JSON and decodes URI components
  const displayOptions = useMemo(() => {
    const parsedValues = parseSelectValue(cellData?.value, glossaryApiId);
    return parsedValues.map((value) => ({
      value: safelyDecodeURIComponent(value),
      label: safelyDecodeURIComponent(value),
    }));
  }, [cellData?.value, glossaryApiId]);

  // Track selected options during editing. When not active, we use displayOptions directly.
  // The ref is necessary because window event listeners (click outside, keyboard) are registered
  // once with getSignal() and capture closure values at registration time. Without the ref,
  // these listeners would always see stale values rather than current selections.

  const loadOptions: LoadOptions<
    SelectOption,
    GroupBase<SelectOption>,
    { page: number; perPage: number }
  > = useCallback(
    async (query, _loadedOptions, additional) => {
      try {
        const page = additional?.page ?? 1;
        const perPage = additional?.perPage ?? 100;

        // Return empty results if no glossary is configured
        if (_isNil(glossaryApiId)) {
          return { options: [], hasMore: false, additional: { page: 1, perPage: 100 } };
        }

        const trimmedQuery = query.trim();
        const queryParams: Record<string, string> = {
          page: String(page),
          perPage: String(perPage),
        };

        if (_isNotEmpty(trimmedQuery)) {
          queryParams.search = trimmedQuery;
        }

        const response = await ApiService.call({
          endpoint: 'GET /api/v1/metadata-glossary/{metadataGlossaryApiId}/options',
          path: { metadataGlossaryApiId: glossaryApiId },
          query: queryParams,
          signal: newAbortController().signal,
          options: {
            onError: {
              slug: 'datatable-select-options-load',
              toast: true,
              throw: true,
              capture: false,
            },
          },
        });

        const { data, links } = response.body!;
        const nextLink = links?.next;
        const hasMore = _notNil(nextLink);

        const options: Array<SelectOption> = data.map((item: SelectOption) => ({
          value: item.label,
          label: item.label,
        }));

        const pagination = hasMore ? parsePaginationFromUrl(nextLink, { page: page + 1, perPage }) : { page, perPage };

        return {
          options,
          hasMore,
          additional: pagination,
        };
      } catch (error) {
        if (notAborted(error)) {
          ExceptionHandler.captureException(
            new InVivoError('Failed to load metadata options', {
              cause: error,
              slug: 'datatable-select-options-load',
              level: 'warning',
              context: {
                ...(_notNil(glossaryApiId) && { metadataGlossaryApiId: glossaryApiId }),
                query: query.trim(),
              },
            })
          );
        }
        return { options: [], hasMore: false, additional: { page: 1, perPage: 100 } };
      }
    },
    [glossaryApiId, newAbortController]
  );

  const persistValue = useCallback(
    (values: Array<SelectOption>, moveDirection?: DataTableDirection) => {
      setActive(false);
      abort('completed');

      if (_notNil(idCoordinate)) {
        const newValue = _isNotEmpty(values) ? JSON.stringify(values.map((v) => encodeURIComponent(v.value))) : null;
        const currentValue = cellData?.value ?? null;

        if (newValue !== currentValue) {
          dataTableService.updateCells([
            {
              column_id: idCoordinate.column,
              row_id: idCoordinate.row,
              value: newValue,
            },
          ]);
        }

        if (!dataTableService.workflowService.workflowActionExit(idCoordinate.column) && _notNil(moveDirection)) {
          dataTableService.moveSelection(moveDirection);
        }
      }
    },
    [idCoordinate, cellData, dataTableService, abort]
  );

  const handleSelectKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.code === 'Enter') {
      keyboardNavigationDirection.current = event.shiftKey ? 'UP' : 'DOWN';
    }
  }, []);

  const handleChange = useCallback(
    (newValue: MultiValue<SelectOption> | SingleValue<SelectOption>) => {
      let values: Array<SelectOption>;
      if (isMulti) {
        values = Array.isArray(newValue) ? [...newValue] : [];
      } else {
        values = _notNil(newValue) ? [newValue as SelectOption] : [];
      }

      setEditingOptions(values);
      selectedOptionsRef.current = values;

      if (!isMulti) {
        // For single-select, check if triggered by keyboard (Enter/Shift+Enter)
        const direction = keyboardNavigationDirection.current;
        keyboardNavigationDirection.current = null;
        persistValue(values, direction ?? undefined);
      }
    },
    [isMulti, persistValue]
  );

  const activateDropdown = useCallback(() => {
    if (!dataTableService.readonly && isEditable && hasOptions) {
      // Initialize editing state from current display value
      setEditingOptions(displayOptions);
      selectedOptionsRef.current = displayOptions;
      setActive(true);
      const signal = getSignal();

      // Close on click outside
      const handleClickOutside = (event: MouseEvent) => {
        if (_notNil(containerRef.current) && !containerRef.current.contains(event.target as Node)) {
          persistValue(selectedOptionsRef.current);
        }
      };

      // Close on escape or enter (for multi-select)
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.code === 'Escape') {
          persistValue(selectedOptionsRef.current);
        } else if (event.code === 'Enter' && isMulti) {
          // Multi-select: handle directly (dropdown stays open until Enter)
          const direction = event.shiftKey ? 'UP' : 'DOWN';
          persistValue(selectedOptionsRef.current, direction);
        }
      };

      window.addEventListener('mousedown', handleClickOutside, { signal, capture: true });
      window.addEventListener('keydown', handleKeyDown, { signal });
    }
  }, [dataTableService.readonly, isEditable, hasOptions, getSignal, persistValue, isMulti, displayOptions]);

  const mouseHandlerOptions = useMemo(() => ({ onDoubleClick: activateDropdown }), [activateDropdown]);

  const { handleMouseDown, handleMouseEnter, handleMouseUp, handleDoubleClick } = useCellMouseHandlers(
    coordinate,
    mouseHandlerOptions
  );

  const handleIconClick = useCallback(() => {
    if (active) {
      persistValue(selectedOptionsRef.current);
    } else {
      activateDropdown();
    }
  }, [active, persistValue, activateDropdown]);

  // Subscribe to cell activation events (e.g., when Enter is pressed)
  useEffect(() => {
    if (_notNil(idCoordinate) && !dataTableService.readonly && isEditable && hasOptions) {
      const abortController = new AbortController();

      const cellSelectionChangeListener = (event: DataTableCellSelectionChangeEvent) => {
        const fromCoordinate = event.detail.selection.from;

        if (
          event.detail.activate &&
          fromCoordinate.column === coordinate.column &&
          fromCoordinate.row === coordinate.row
        ) {
          activateDropdown();
        }
      };

      dataTableService.subscribe(
        DataTableEvent.CellSelectionChange,
        cellSelectionChangeListener,
        abortController.signal
      );

      return () => {
        abortController.abort();
      };
    }
  }, [dataTableService, coordinate, idCoordinate, isEditable, hasOptions, activateDropdown]);

  const chips = useMemo(() => {
    if (_isEmpty(displayOptions)) {
      return (
        <span className={styles.placeholder} data-test-element="placeholder">
          &nbsp;
        </span>
      );
    }

    return displayOptions.map((option) => (
      <span key={option.value} className={styles.chip} data-test-element="chip">
        {option.label}
      </span>
    ));
  }, [displayOptions]);

  return (
    <div
      ref={containerRef}
      className={cn(tableStyles.CellContainer, { [tableStyles.groupBorder]: isGroupBoundary })}
      style={containerStyle}
      data-test-component="MetadataSelectCell"
      data-test-element="container"
      data-test-key={`(${coordinate.column},${coordinate.row})`}
    >
      {cellState === 'Loading' ? (
        <div className={cn(tableStyles.Cell, tableStyles.Loading)} data-test-element="loading-cell" />
      ) : (
        <Tooltip render={tooltip}>
          <div
            className={cn(
              tableStyles.Cell,
              tableStyles[selectionState],
              { [tableStyles.Active]: active },
              tableStyles[cellState]
            )}
            style={contentStyle}
            data-state={cellState}
            data-test-element="cell"
            onMouseDown={handleMouseDown}
            onMouseEnter={handleMouseEnter}
            onMouseUp={handleMouseUp}
            onDoubleClick={handleDoubleClick}
          >
            <div className={styles.container}>
              <div className={styles.valuesContainer}>{chips}</div>
              {isEditable && hasOptions && (
                <div className={styles.dropdownIcon} onClick={handleIconClick} data-test-element="dropdown-icon">
                  <Icon icon="chev_down" width={10} height={10} />
                </div>
              )}
            </div>

            {active && hasOptions && (
              <div className={styles.selectDropdown}>
                <AsyncPaginate
                  value={isMulti ? editingOptions : (editingOptions[0] ?? null)}
                  loadOptions={loadOptions}
                  onChange={handleChange}
                  onKeyDown={handleSelectKeyDown}
                  additional={INITIAL_PAGINATION}
                  isMulti={isMulti}
                  closeMenuOnSelect={!isMulti}
                  autoFocus
                  defaultOptions
                  menuIsOpen
                  debounceTimeout={300}
                  classNamePrefix="datatable-select"
                  placeholder="Search..."
                  maxMenuHeight={300}
                  components={SELECT_COMPONENTS}
                />
              </div>
            )}
          </div>
        </Tooltip>
      )}
    </div>
  );
};
